/* eslint-disable @typescript-eslint/explicit-module-boundary-types */
import ActionConstants from '../constants';
import { getDisputeListAPI, getDisputeDetailsAPI, getEdgeDisputeListDownloadAPI, getDisputeDocumentConfigAPI, uploadChargebackDocumentAPI, reUploadChargebackDocumentAPI, getDisputesFeatureFlagAPI } from '../../api/disputes';
import { getOrderDetailsV3API, getOrderListV3API } from '../../api/edgeTransactions';
import { DisputeFilters, DisputeListAPIPayload, ChargebackDefendModalState } from 'screens/Products/Edge/Disputes/typing';
import { downloadCsvAction } from './downloadCsv';
import { getCancelableRequestMeta } from './cancelableRequest';

type LoaderOptions = {
  hideLoader?: boolean;
  loaderMessage?: string;
};

export const getDisputeListAction = (
  data: DisputeListAPIPayload,
  options?: LoaderOptions
) => {
  const { requestData, hideLoader, loaderMessage, cancelKey, requestId } =
    getCancelableRequestMeta(
      { ...(data || {}), ...(options || {}) },
      ActionConstants.GET_DISPUTE_LIST
    );
  const payload = {
    serviceMethod: getDisputeListAPI.bind(null, requestData),
    actionTypeSuccess: ActionConstants.GET_DISPUTE_LIST_SUCCESS,
    actionTypeFailure: ActionConstants.GET_DISPUTE_LIST_FAILURE,
    actionTypeRequest: ActionConstants.GET_DISPUTE_LIST_REQUEST,
    hideLoader,
    loaderMessage,
    cancelKey,
    extra: { requestId },
  };
  return { type: ActionConstants.GET_DISPUTE_LIST, payload };
};

export const clearDisputeListAction = () => {
  return { type: ActionConstants.GET_DISPUTE_LIST_CLEAR };
};

export const getDisputeDetailsAction = (chargebackId: string) => {
  const { requestData, hideLoader, loaderMessage, cancelKey, requestId } =
    getCancelableRequestMeta(
      { chargebackId },
      ActionConstants.GET_DISPUTE_DETAILS,
      false
    );
  const payload = {
    serviceMethod: getDisputeDetailsAPI.bind(null, requestData),
    actionTypeSuccess: ActionConstants.GET_DISPUTE_DETAILS_SUCCESS,
    actionTypeFailure: ActionConstants.GET_DISPUTE_DETAILS_FAILURE,
    actionTypeRequest: ActionConstants.GET_DISPUTE_DETAILS_REQUEST,
    hideLoader,
    loaderMessage,
    cancelKey,
    extra: { requestId },
  };
  return { type: ActionConstants.GET_DISPUTE_DETAILS, payload };
};

export const clearDisputeDetailsAction = () => {
  return { type: ActionConstants.GET_DISPUTE_DETAILS_CLEAR };
};

export const getDisputeOrderDetailsV3Action = ({
  orderId,
  merchantVersion,
  transactionDate,
  orderIdQuery,
}: {
  orderId: string;
  merchantVersion: string;
  transactionDate: string;
  orderIdQuery: string;
}) => {
  const { requestData, hideLoader, loaderMessage, cancelKey, requestId } =
    getCancelableRequestMeta(
      { orderId, merchantVersion, transactionDate, orderIdQuery },
      ActionConstants.GET_DISPUTE_ORDER_DETAILS_V3
    );
  const payload = {
    serviceMethod: getOrderDetailsV3API.bind(
      null,
      requestData
    ),
    actionTypeSuccess: ActionConstants.GET_DISPUTE_ORDER_DETAILS_V3_SUCCESS,
    actionTypeFailure: ActionConstants.GET_DISPUTE_ORDER_DETAILS_V3_FAILURE,
    actionTypeRequest: ActionConstants.GET_DISPUTE_ORDER_DETAILS_V3_REQUEST,
    hideLoader,
    loaderMessage,
    cancelKey,
    extra: { requestId },
  };
  return { type: ActionConstants.GET_DISPUTE_ORDER_DETAILS_V3, payload };
};

export const clearDisputeOrderDetailsV3Action = () => {
  return { type: ActionConstants.GET_DISPUTE_ORDER_DETAILS_V3_CLEAR };
};

export const getDisputeOrderListV3Action = (payloadBase64: string) => {
  const { requestData, hideLoader, loaderMessage, cancelKey, requestId } =
    getCancelableRequestMeta(
      { payload: payloadBase64 },
      ActionConstants.GET_DISPUTE_ORDER_LIST_V3
    );
  const payload = {
    serviceMethod: getOrderListV3API.bind(null, requestData),
    actionTypeSuccess: ActionConstants.GET_DISPUTE_ORDER_LIST_V3_SUCCESS,
    actionTypeFailure: ActionConstants.GET_DISPUTE_ORDER_LIST_V3_FAILURE,
    actionTypeRequest: ActionConstants.GET_DISPUTE_ORDER_LIST_V3_REQUEST,
    hideLoader,
    loaderMessage,
    cancelKey,
    extra: { requestId },
  };
  return { type: ActionConstants.GET_DISPUTE_ORDER_LIST_V3, payload };
};

export const clearDisputeOrderListV3Action = () => {
  return { type: ActionConstants.GET_DISPUTE_ORDER_LIST_V3_CLEAR };
};

export const getDisputeDownloadUrlAction = (
  data: DisputeListAPIPayload,
  fileName: string
) => {
  return downloadCsvAction({
    serviceMethod: getEdgeDisputeListDownloadAPI.bind(null, data),
    fileName,
    url: '',
  });
};

// Filter state management actions
export const setDisputeFiltersAction = (filters: Partial<DisputeFilters>) => {
  return { type: ActionConstants.SET_DISPUTE_FILTERS, payload: filters };
};

export const clearDisputeFiltersAction = () => {
  return { type: ActionConstants.CLEAR_DISPUTE_FILTERS };
};

export const getDisputeDocumentListAction = () => {
  const { requestData, hideLoader, loaderMessage, cancelKey, requestId } =
    getCancelableRequestMeta({}, ActionConstants.GET_DISPUTE_DOCUMENT_CONFIG);
  const payload = {
    serviceMethod: getDisputeDocumentConfigAPI.bind(null, requestData),
    actionTypeSuccess: ActionConstants.GET_DISPUTE_DOCUMENT_CONFIG_SUCCESS,
    actionTypeFailure: ActionConstants.GET_DISPUTE_DOCUMENT_CONFIG_FAILURE,
    actionTypeRequest: ActionConstants.GET_DISPUTE_DOCUMENT_CONFIG_REQUEST,
    hideLoader,
    loaderMessage,
    cancelKey,
    extra: { requestId },
  };
  return { type: ActionConstants.GET_DISPUTE_DOCUMENT_CONFIG, payload };
};
export const uploadChargeBackDocuments = (chargebackId: string, formData: FormData) => {
  const payload = {
    serviceMethod: uploadChargebackDocumentAPI.bind(null, chargebackId, formData),
    actionTypeSuccess: ActionConstants.UPLOAD_CHARGEBACK_DOCUMENT_SUCCESS,
    actionTypeFailure: ActionConstants.UPLOAD_CHARGEBACK_DOCUMENT_FAILURE,
    actionTypeRequest: ActionConstants.UPLOAD_CHARGEBACK_DOCUMENT_REQUEST,
  };
  return { type: ActionConstants.UPLOAD_CHARGEBACK_DOCUMENT, payload };
};

// Chargeback Defend Modal State Management
export const setChargebackDefendModalAction = (modalState: Partial<ChargebackDefendModalState>) => {
  return { type: ActionConstants.SET_CHARGEBACK_DEFEND_MODAL, payload: modalState };
};

export const clearChargebackDefendModalAction = () => {
  return { type: ActionConstants.CLEAR_CHARGEBACK_DEFEND_MODAL };
};

// Clear Upload Document State
export const clearUploadDocumentStateAction = () => {
  return { type: ActionConstants.UPLOAD_CHARGEBACK_DOCUMENT_CLEAR };
};

// Reupload Chargeback Documents
export const reuploadChargeBackDocuments = (chargebackId: string, formData: FormData) => {
  const payload = {
    serviceMethod: reUploadChargebackDocumentAPI.bind(null, chargebackId, formData),
    actionTypeSuccess: ActionConstants.REUPLOAD_CHARGEBACK_DOCUMENT_SUCCESS,
    actionTypeFailure: ActionConstants.REUPLOAD_CHARGEBACK_DOCUMENT_FAILURE,
    actionTypeRequest: ActionConstants.REUPLOAD_CHARGEBACK_DOCUMENT_REQUEST,
  };
  return { type: ActionConstants.REUPLOAD_CHARGEBACK_DOCUMENT, payload };
};

// Clear Reupload Document State
export const clearReuploadDocumentStateAction = () => {
  return { type: ActionConstants.REUPLOAD_CHARGEBACK_DOCUMENT_CLEAR };
};

// Disputes feature flag — checks if disputes feature is enabled for the current MID
export const getDisputesFeatureFlagAction = () => {
  const payload = {
    serviceMethod: getDisputesFeatureFlagAPI,
    actionTypeSuccess: ActionConstants.GET_DISPUTES_FEATURE_FLAG_SUCCESS,
    actionTypeFailure: ActionConstants.GET_DISPUTES_FEATURE_FLAG_FAILURE,
    actionTypeRequest: ActionConstants.GET_DISPUTES_FEATURE_FLAG_REQUEST,
    hideLoader: true,
  };
  return { type: ActionConstants.GET_DISPUTES_FEATURE_FLAG, payload };
};
